"use client";
import { IBaseLabelValue } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import { cn } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import Link from "next/link";
import PdfTextTemplate from "@/components/pdfTemplates/pdfTextTemplate";
import { useRef } from "react";
import { IRawPurchasedSanaInquiryResData } from "@/interfaces/inquiries/governmental";

const SanaInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedSanaInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const registered = data.data.data.data;
  const sanaInput = data.data.input;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("sanaInquiry.nationalCode"),
      value: sanaInput.national_code,
    },
    {
      label: t("sanaInquiry.mobileNumber"),
      value: sanaInput.mobile_number,
    },
  ];

  const pdfContentRef = useRef<HTMLDivElement>(null);
  const resultContent = registered ? (
    <p className="text-justify text-sm md:text-base">
      بر اساس استعلام انجام‌شده در سامانه نوین، فرد مورد نظر با کد ملی واردشده
      در سامانه ثنا (سامانه ثبت‌نام الکترونیکی قوه قضاییه) ثبت‌نام کرده است. این
      به معنای آن است که
      <span className="mx-1 font-semibold" style={{ color: "#00a63e" }}>
        &nbsp; فرد دارای حساب کاربری فعال در سامانه ثنا بوده
      </span>{" "}
      و قادر به دریافت ابلاغیه‌های قضایی به صورت الکترونیکی می‌باشد.
    </p>
  ) : (
    <p className="text-justify text-sm md:text-base">
      بر اساس استعلام انجام‌شده در سامانه نوین، فرد مورد نظر با کد ملی واردشده
      در سامانه ثنا (سامانه ثبت‌نام الکترونیکی قوه قضاییه)
      <span className="mx-1 font-semibold" style={{ color: "#c10007" }}>
        ثبت‌نام نکرده است
      </span>
      . پیشنهاد می‌شود در صورت نیاز، فرد نسبت به ثبت‌نام در سامانه ثنا از طریق
      وب‌سایت رسمی به آدرس
      <a
        href={"https://sana.adliran.ir/"}
        target="_blank"
        className="mx-1"
        style={{ color: "#155dfc" }}
      >
        sana.adliran.ir{" "}
      </a>
      یا مراجعه به دفاتر خدمات قضایی اقدام نماید.
    </p>
  );
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("sanaInquiry.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="sana-inquiry-download-pdf"
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            fileName={`نتیجه استعلام وضعیت ثنا ${sanaInput.national_code}`}
          />
        </FlexBetween>
        <div className="shadow-main mb-10 flex flex-col gap-6 rounded-xl p-4 py-8 sm:p-8 md:gap-8">
          {registered ? (
            <p className="text-justify text-sm md:text-base">
              بر اساس استعلام انجام‌شده در سامانه نوین، فرد مورد نظر با کد ملی
              واردشده در سامانه ثنا (سامانه ثبت‌نام الکترونیکی قوه قضاییه)
              ثبت‌نام کرده است. این به معنای آن است که
              <span className="mx-1 font-semibold text-green-600">
                &nbsp; فرد دارای حساب کاربری فعال در سامانه ثنا بوده
              </span>{" "}
              و قادر به دریافت ابلاغیه‌های قضایی به صورت الکترونیکی می‌باشد.
            </p>
          ) : (
            <p className="text-justify text-sm md:text-base">
              بر اساس استعلام انجام‌شده در سامانه نوین، فرد مورد نظر با کد ملی
              واردشده در سامانه ثنا (سامانه ثبت‌نام الکترونیکی قوه قضاییه)
              <span className="mx-1 font-semibold text-red-700">
                ثبت‌نام نکرده است
              </span>
              . پیشنهاد می‌شود در صورت نیاز، فرد نسبت به ثبت‌نام در سامانه ثنا
              از طریق وب‌سایت رسمی به آدرس
              <Link
                href={"https://sana.adliran.ir/"}
                target="_blank"
                className="mx-1 text-blue-600"
              >
                sana.adliran.ir{" "}
              </Link>
              یا مراجعه به دفاتر خدمات قضایی اقدام نماید.
            </p>
          )}
        </div>
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="sana-inquiry-download-pdf"
            fileName={`نتیجه استعلام وضعیت ثنا ${sanaInput.national_code}`}
            htmlContentRef={pdfContentRef}
            useHtmlToPdf={true}
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-sana-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-sana-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Sana_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTextTemplate
          title={`نتیجه استعلام وضعیت ثنا ${sanaInput.national_code}`}
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default SanaInquiryDetails;
